import { Component, Inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import { find, map } from 'lodash';
import { Subject } from 'rxjs';
import { StudentSupportModal } from 'Src/app/shared/modals/student-support/student-support-modal.service';
import { ImStudentSupport } from '../../../../app/shared/static-models/im-models/im-student-support.service';
import { StudentSupportStatuses } from '../../../shared/constants/student-support-statuses.constant';
import { ImUser } from '../../../shared/services/im-models/im-user';
import {
  IStudentSupport,
  TValidStudentSupportBackendStatuses,
} from '../../../shared/typings/interfaces/student-support.interface';
import { IStudent } from '../../../shared/typings/interfaces/student.interface';
import { ISchoolSupport } from '../../../shared/typings/interfaces/support.interface';
import { IUser } from '../../../shared/typings/interfaces/user.interface';
import { UpdateStudentSupport } from '../../../store';
import { StudentPanelService } from '../student-panel.service';
import { ImSupport } from './../../../../app/shared/static-models/im-models/im-support.service';
import { SupportCategories } from './../../../shared/constants/support-categories.constant';
import { ApiService } from './../../../shared/services/api-service/api-service';
import { DateHelpers } from './../../../shared/services/date-helpers/date-helpers.service';
import { IUpdatePayload } from './../../../store/actions/flag-actions';
import SupportsPanelConfig from './student-supports-panel-v3.config';

interface IFormattedSupportData {
  year: string;
  type: string;
  name: string;
  lead: string;
  leadTooltip: string;
  durationTooltip: string;
  duration: string;
  details: string;
  detailsTooltip: string;
  id: string;
  exam: string;
}

@Component({
  selector: 'student-supports-panel-v3',
  template: require('./student-supports-panel-v3.component.html'),
  styles: [require('./student-supports-panel-v3.scss')],
  providers: [DateHelpers],
})
export class StudentSupportsPanelV3 implements OnInit, OnChanges {
  @Input()
  student: IStudent;
  @Input()
  studentSupports: IStudentSupport[];
  @Input()
  supports: ISchoolSupport[];
  @Input()
  collapsed: boolean;
  @Input()
  currentUser: IUser;
  panelTitle: string;

  // local
  completedSupportsColumns: string[];
  inProgressSupports: IFormattedSupportData[];
  hasInProgress: boolean;
  inProgressSupportsData: MatTableDataSource<IFormattedSupportData>;
  completedSupports: IFormattedSupportData[];
  hasCompleted: boolean;
  completedSupportsData: MatTableDataSource<IFormattedSupportData>;
  notStartedSupports: IFormattedSupportData[];
  hasNotStartedSupports: boolean;
  notStartedSupportsData: MatTableDataSource<IFormattedSupportData>;
  studentName: string;
  isViewUser: boolean;
  noSupportsCreated: boolean;

  public destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    @Inject('ImSupport') private imSupport: ImSupport,
    @Inject('ImStudentSupport') private imStudentSupport: ImStudentSupport,
    private imUser: ImUser,
    @Inject('StudentSupportModal') private studentSupportModal: StudentSupportModal,
    @Inject('Rollbar') private rollbar,
    private dateHelpers: DateHelpers,
    private studentPanel: StudentPanelService,
    private store: Store<any>,
    private apiService: ApiService,
  ) {}

  // TODO: make ngOnInit more modular so that the `ngOnChanges` event doesn't
  // execute all of the logic found in the `ngOnInit` (CM).
  ngOnInit() {
    this.panelTitle = SupportsPanelConfig.title;
    this.collapsed = this.studentPanel.getInitialPanelState('student-supports-panel');
    this.isViewUser = this.imUser.isViewType(this.currentUser);
    const { first, last } = this.student.studentDetails.name;
    this.studentName = `${first} ${last}`;

    this.completedSupportsColumns = ['supportCategory', 'supportName', 'with', 'duration', 'details'];

    this.setSupportData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.studentSupports) {
      const { studentSupports } = changes;
      if (!studentSupports.firstChange) {
        this.studentSupports = studentSupports.currentValue;
        this.ngOnInit();
      }
    }
  }

  get supportsColumns() {
    const supportsColumns = ['supportCategory', 'supportName', 'with', 'duration', 'details'];
    // we only add the `menu` column if the user can edit (CM).
    if (!this.isViewUser) supportsColumns.push('menu');

    return supportsColumns;
  }

  /* istanbul ignore next */
  truncate(text) {
    if (text.length > 10) return text.substring(0, 9) + '...';
    else return text;
  }

  togglePanelBody(): void {
    this.collapsed = this.studentPanel.togglePanelBody(this.collapsed, this.panelTitle);
  }

  /* istanbul ignore next */
  updateStudentSupportStatus(id: string, status: TValidStudentSupportBackendStatuses): void {
    const patch: IUpdatePayload<IStudentSupport> = {
      id,
      patch: { status },
    };
    this.store.dispatch(new UpdateStudentSupport(patch));
  }

  openEditModal(supportId: string, exam: string) {
    const { schoolId, _id } = this.student;
    this.studentSupportModal.show(schoolId, _id, supportId, null, true);
  }

  /* istanbul ignore next */
  setSupportData() {
    const studentSupportsGroupedByFrontEndStatus = this.imStudentSupport.getStudentSupportsGroupedByFrontEndStatus(
      this.studentSupports,
    );

    const inProgressStudentSupports =
      studentSupportsGroupedByFrontEndStatus[StudentSupportStatuses.frontend.IN_PROGRESS];

    const notStartedStudentSupports =
      studentSupportsGroupedByFrontEndStatus[StudentSupportStatuses.frontend.NOT_STARTED];

    const completedStudentSupports = studentSupportsGroupedByFrontEndStatus[StudentSupportStatuses.frontend.COMPLETED];

    const promise1 = this.getFormattedSupportData(inProgressStudentSupports)
      .then(formattedSupportData => {
        this.inProgressSupports = formattedSupportData;
        this.hasInProgress = this.inProgressSupports.length > 0;
        this.inProgressSupportsData = new MatTableDataSource(this.inProgressSupports);
      })
      .catch(this.rollbar.error.bind(this));

    const promise2 = this.getFormattedSupportData(notStartedStudentSupports)
      .then(formattedSupportData => {
        this.notStartedSupports = formattedSupportData;
        this.hasNotStartedSupports = this.notStartedSupports.length > 0;
        this.notStartedSupportsData = new MatTableDataSource(this.notStartedSupports);
      })
      .catch(this.rollbar.error.bind(this));

    const promise3 = this.getFormattedSupportData(completedStudentSupports)
      .then(formattedSupportData => {
        this.completedSupports = formattedSupportData;
        this.hasCompleted = this.completedSupports.length > 0;
        this.completedSupportsData = new MatTableDataSource(this.completedSupports);
      })
      .catch(this.rollbar.error.bind(this));

    Promise.all([promise1, promise2, promise3]).then(() => {
      this.noSupportsCreated = !this.hasInProgress && !this.hasNotStartedSupports && !this.hasCompleted;
    });
  }

  /* istanbul ignore next */
  async getFormattedSupportData(studentSupports) {
    const formattedSupportData = await Promise.all(
      map(studentSupports, async (studentSupport: IStudentSupport) => {
        const { supportId } = studentSupport.support;
        let support = find(this.supports, (support: ISchoolSupport) => {
          return support._id === supportId;
        });

        if (!support) {
          support = await this.apiService.getSupport(supportId).toPromise();
        }

        const { schedule, activityLead, location, category, name } = support;

        const startsOn = this.dateHelpers.moment(studentSupport.startsOn);
        const endsOn = this.dateHelpers.moment(schedule.endsOn);
        const schoolYear = this.imSupport.getFormattedSchoolYear(support);
        const supportDuration = endsOn.diff(startsOn, 'weeks');
        const durationStart = startsOn.format('MMM DD');
        const durationEnd = endsOn.format('MMM DD');
        const { startTime } = schedule;
        const daysOfWeek = this.imSupport.getMeetsOnScheduleString(support);
        const type = SupportCategories[category].humanShort;
        // const type = category === 'ATTENDANCE' ? 'Att' : 'Acad';
        const leadName = `${activityLead.firstName} ${activityLead.lastName}`;
        const exam = support.category === 'REGENTS_PREP' ? support.metaData.examSubject : null;

        const completedSupport: IFormattedSupportData = {
          year: schoolYear,
          type,
          name,
          lead: this.truncate(leadName),
          leadTooltip: leadName === this.truncate(leadName) ? null : leadName,
          durationTooltip: `${supportDuration} weeks`,
          duration: `${durationStart}-${durationEnd}`,
          details: location || `&mdash;`,
          detailsTooltip: `${daysOfWeek} at ${startTime}`,
          id: studentSupport._id,
          exam,
        };

        return completedSupport;
      }),
    );

    return formattedSupportData;
  }
}
